import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./tour.scss";

const CARD_PAD = 8;
const RING_PAD = 5;
const TOOLTIP_WIDTH = 340;
const TOOLTIP_HEIGHT_ESTIMATE = 230;
const TOOLTIP_GAP = 16;
const VIEWPORT_MARGIN = 16;
const HEADER_CLEARANCE = 88;
const TOOLTIP_CLEARANCE = 260;
// At/below this width the tooltip becomes a bottom sheet (no room to sit
// beside a full-width card), and scrolling reserves the bottom for it.
const MOBILE_MAX = 600;
// A mobile TOP sheet is only worthwhile when there's this much card content
// BELOW the highlighted control to reveal (e.g. a section header above its
// list). For a small standalone control (card === target, like a search or
// filter button) there's nothing below, so a bottom sheet is used instead.
const SHEET_TOP_MIN_BELOW = 120;
const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const MEASURE_RETRY_DELAYS = [120, 400, 900];

/**
 * Generic spotlight/highlight product-tour overlay, ported from the
 * MapMyPay mobile tour mockup. Dims the page, draws a bordered "ring"
 * around the current step's target element, and shows a tooltip with
 * Skip / Next / Done controls. Renders through a portal into
 * document.body and measures targets in viewport coordinates so it sits
 * above the persistent header/sidebar as well as the scrollable content.
 *
 * steps: Array<{ cardSelector, targetSelector, icon?, title, text }>
 */
export default function GuidedTour({
  isOpen,
  steps,
  onDone,
  scrollContainerSelector,
}) {
  const [step, setStep] = useState(0);
  const [geo, setGeo] = useState(null);
  // Measured bounding box of the rendered tooltip — the pointer arrow anchors
  // to its real edges so it can sit correctly on ANY side (see effect below).
  const [tipRect, setTipRect] = useState(null);
  const tooltipRef = useRef(null);
  const stepRef = useRef(step);
  stepRef.current = step;
  // Timestamp of the tour's most recent programmatic scroll. The scroll
  // lock below uses it to tell "the tour just moved the page" apart from
  // "the user grabbed the scrollbar", so it only snaps back the latter.
  const tourScrollAtRef = useRef(0);

  // Reset to step 0 whenever the tour (re)opens — e.g. the header's
  // Replay button reopening it after a previous run left `step` on some
  // later index. Done during render (React's documented "adjust state
  // when a prop changes" pattern) rather than in a useEffect: an effect
  // would fire the measurement layoutEffect below FIRST, with the stale
  // step still in place, causing it to briefly scroll to and highlight
  // the wrong (previous run's) target before correcting itself.
  const prevIsOpenRef = useRef(isOpen);
  if (isOpen !== prevIsOpenRef.current) {
    prevIsOpenRef.current = isOpen;
    if (isOpen && step !== 0) {
      setStep(0);
    }
  }

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const current = steps[step];
    if (!current) return undefined;

    let cancelled = false;
    let found = false;
    const timers = [];

    // Resolve a selector to the first VISIBLE match. Responsive layouts often
    // render the same logical control twice — a desktop copy and a mobile
    // copy — with one `display:none`. A plain querySelector would grab the
    // hidden one (whose getBoundingClientRect is all zeros), stranding the
    // ring in the top-left corner; skipping zero-size matches picks the copy
    // that's actually on screen.
    const resolve = (selector) => {
      if (!selector) return null;
      const els = document.querySelectorAll(selector);
      const sized = [];
      for (let i = 0; i < els.length; i += 1) {
        const r = els[i].getBoundingClientRect();
        if (r.width > 0 && r.height > 0) sized.push([els[i], r]);
      }
      if (sized.length === 0) return els[0] || null;
      // A single element (incl. a legit below-the-fold target that just
      // needs scrolling) is returned as-is. Only when there are MULTIPLE
      // sized copies — a responsive layout's desktop + mobile duplicates —
      // prefer the one currently intersecting the viewport, so an off-screen
      // slide-in panel (still sized) doesn't steal the ring.
      if (sized.length === 1) return sized[0][0];
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      for (let i = 0; i < sized.length; i += 1) {
        const r = sized[i][1];
        if (r.right > 0 && r.left < vw && r.bottom > 0 && r.top < vh) {
          return sized[i][0];
        }
      }
      return sized[0][0];
    };

    const measure = () => {
      if (cancelled || stepRef.current !== step) return;
      const cardEl = resolve(current.cardSelector);
      const targetEl = resolve(current.targetSelector || current.cardSelector);
      if (!cardEl || !targetEl) return;

      found = true;
      const cardRect = cardEl.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();

      setGeo({
        card: {
          top: cardRect.top - CARD_PAD,
          left: cardRect.left - CARD_PAD,
          width: cardRect.width + CARD_PAD * 2,
          height: cardRect.height + CARD_PAD * 2,
        },
        target: {
          top: targetRect.top - RING_PAD,
          left: targetRect.left - RING_PAD,
          width: targetRect.width + RING_PAD * 2,
          height: targetRect.height + RING_PAD * 2,
        },
        viewportW: window.innerWidth,
        viewportH: window.innerHeight,
      });
    };

    const scrollParent = scrollContainerSelector
      ? document.querySelector(scrollContainerSelector)
      : null;

    // Bring the target into view within any horizontally-scrollable ancestor
    // (e.g. a filter-chip row / card rail that scrolls sideways) — the tour's
    // own vertical scroll can't reveal a control that's scrolled off to the
    // side. Centres the target within each such ancestor.
    const revealHorizontally = (el) => {
      let node = el.parentElement;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const scrollsX =
          (style.overflowX === "auto" || style.overflowX === "scroll") &&
          node.scrollWidth > node.clientWidth + 1;
        if (scrollsX) {
          const nodeRect = node.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          if (elRect.left < nodeRect.left || elRect.right > nodeRect.right) {
            const elCenter = elRect.left + elRect.width / 2;
            const nodeCenter = nodeRect.left + nodeRect.width / 2;
            node.scrollLeft += elCenter - nodeCenter;
          }
        }
        node = node.parentElement;
      }
    };

    const scrollIntoView = () => {
      const targetEl = resolve(current.targetSelector || current.cardSelector);
      if (!targetEl) return;
      const cardEl = resolve(current.cardSelector);

      revealHorizontally(targetEl);

      const viewH = window.innerHeight;
      const isMobile = window.innerWidth <= MOBILE_MAX;
      const topSafe = HEADER_CLEARANCE + 16;
      const cardRect = cardEl ? cardEl.getBoundingClientRect() : null;

      let delta = 0;
      if (isMobile) {
        // On mobile the tooltip is a sheet on one edge. Use a TOP sheet only
        // when the highlighted control sits near the very top of its section
        // (so showing the section from its top keeps the control right under
        // the sheet — e.g. a section header / "See all" link). Otherwise use
        // a BOTTOM sheet and scroll the TARGET to sit just above it, so the
        // ring is always adjacent to the tooltip — even for a control deep in
        // a tall card (e.g. the "Living Location" header mid-form).
        const tRect = targetEl.getBoundingClientRect();
        const cRect = cardRect || tRect;
        const sheetSpace = TOOLTIP_HEIGHT_ESTIMATE + VIEWPORT_MARGIN * 2;
        const rel = (tRect.top - cRect.top) / Math.max(cRect.height, 1);
        const contentBelowTarget = cRect.bottom - tRect.bottom;
        const sheetOnTop =
          rel < 0.28 && contentBelowTarget > SHEET_TOP_MIN_BELOW;
        if (sheetOnTop) {
          // Card starts just under the top sheet; shows from its top down.
          const contentTop = HEADER_CLEARANCE + sheetSpace;
          delta = cRect.top - contentTop;
        } else {
          const zoneBottom = viewH - sheetSpace;
          if (cRect.height <= zoneBottom - topSafe) {
            // Whole section fits above the bottom sheet — show all of it.
            delta = cRect.bottom - zoneBottom;
          } else {
            // Section too tall — keep the control just above the sheet.
            delta = tRect.bottom - (zoneBottom - VIEWPORT_MARGIN);
          }
        }
      } else {
        const availH = viewH - topSafe - VIEWPORT_MARGIN;
        if (cardRect && cardRect.height <= availH) {
          // The whole section fits — align its top just under the header.
          // Always pull it up to topSafe (not only when off-screen) so there
          // is maximum room BELOW it for the tooltip; a section sitting
          // mid-viewport (pushed down by a nudge/title) would otherwise leave
          // no clean side and force the tooltip to overlap it.
          if (Math.abs(cardRect.top - topSafe) > 2) {
            delta = cardRect.top - topSafe;
          }
        } else {
          // Section taller than the viewport — anchor on the specific target
          // so the highlighted control stays on-screen.
          const rect = targetEl.getBoundingClientRect();
          if (
            rect.top < HEADER_CLEARANCE ||
            rect.bottom > viewH - TOOLTIP_CLEARANCE
          ) {
            delta = rect.top - (HEADER_CLEARANCE + 24);
          }
        }
      }

      if (Math.abs(delta) < 1) return;
      // Mark this as a tour-initiated scroll so the scroll lock adopts
      // (rather than reverts) the new position.
      tourScrollAtRef.current = Date.now();
      if (scrollParent) {
        scrollParent.scrollTop += delta;
      } else {
        // Force an INSTANT jump, overriding any `scroll-behavior: smooth`
        // that may apply to the document. A smooth (animated) scroll
        // finishes over several frames, so the measure() call right after
        // would read the target mid-animation and freeze the ring at an
        // intermediate spot; an instant jump makes the next measure exact.
        const rootEl = document.scrollingElement || document.documentElement;
        const prevBehavior = rootEl.style.scrollBehavior;
        rootEl.style.scrollBehavior = "auto";
        window.scrollBy(0, delta);
        rootEl.style.scrollBehavior = prevBehavior;
      }
    };

    // Let a step run a side-effect the moment it becomes active — e.g. flip a
    // page's billing toggle so the plan cards below re-render with annual
    // pricing before we measure them. Optional and backward-compatible: steps
    // without `onEnter` are unaffected. The re-measure passes below pick up any
    // layout change the side-effect causes.
    if (typeof current.onEnter === "function") current.onEnter();

    // Some targets live inside a collapsed panel (e.g. a "mortgage details"
    // accordion). A step can name an `openSelector` to click first; only do
    // so when the target isn't already present, so we expand rather than
    // toggle a panel that's already open.
    if (current.openSelector && !resolve(current.targetSelector)) {
      const opener = document.querySelector(current.openSelector);
      if (opener) opener.click();
    }

    // Deliberately DON'T blank the previous step's geometry here. measure()
    // runs synchronously right below and repoints the ring to the new target
    // in the same render (the ring then glides via its CSS transition). If
    // the new target is missing (e.g. a Follow button that isn't rendered on
    // your own post), the old ring/tooltip stays visible until the step is
    // auto-skipped — so the tour never blanks out and looks "finished".
    scrollIntoView();
    measure();
    // Re-measure a few times after the scroll: the dashboard's sections
    // load asynchronously (Hospital map tiles, jobs, community) and can
    // shift layout for a moment after the initial jump, which would leave
    // the ring/tooltip a little off. Each pass re-reads live DOM geometry.
    [120, 350, 700].forEach((delay) => {
      timers.push(setTimeout(measure, delay));
    });

    // The dashboard's sections can still be loading data (skeletons)
    // when the tour opens, so the target for a later step may not exist
    // yet. Keep retrying briefly, then skip the step rather than leaving
    // the tour stuck on an invisible overlay.
    MEASURE_RETRY_DELAYS.forEach((delay) => {
      timers.push(
        setTimeout(() => {
          if (cancelled || found) return;
          scrollIntoView();
          measure();
        }, delay),
      );
    });
    // If the target still isn't found shortly after the retries, auto-skip
    // to the next step. Kept short so a genuinely-absent target (e.g. a
    // missing Follow button) advances quickly instead of dwelling.
    timers.push(
      setTimeout(() => {
        if (cancelled || found) return;
        setStep((v) => (v < steps.length - 1 ? v + 1 : v));
        if (step >= steps.length - 1) onDone();
      }, 1000),
    );

    // Keep the ring/tooltip glued to the target if anything moves it —
    // window resize, or any residual scrolling (the tour's own instant
    // jumps re-confirm harmlessly; a late async layout shift is caught).
    const scrollTarget = scrollParent || window;
    window.addEventListener("resize", measure);
    scrollTarget.addEventListener("scroll", measure, { passive: true });
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", measure);
      scrollTarget.removeEventListener("scroll", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, scrollContainerSelector]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onDone();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onDone]);

  // Lock USER scroll while the tour is open — the user should only move
  // between sections via Next/Skip/Done, not by scrolling out from under
  // the spotlight. Deliberately NOT `body.style.overflow = "hidden"` (the
  // pattern other modals here use): this page's scrollable element is the
  // window itself, so hiding its overflow would also freeze the tour's own
  // scrollIntoView calls that move the page between steps.
  //
  // Two layers, so the tour's programmatic scrolling still works:
  //  1. Block scroll INPUT — wheel, touch drag, and the scroll keys —
  //     with preventDefault. Programmatic scrollBy/scrollTop is unaffected
  //     by these, and this is immune to any async/smooth-scroll timing.
  //  2. Snap back the scrollbar drag (which input-blocking can't catch),
  //     but ignore scroll events that land within a short window after a
  //     tour-initiated scroll so it never fights the tour's own movement.
  useEffect(() => {
    if (!isOpen) return undefined;
    const scrollEl = scrollContainerSelector
      ? document.querySelector(scrollContainerSelector)
      : null;
    const getY = () => (scrollEl ? scrollEl.scrollTop : window.scrollY);
    const setY = (y) => {
      if (scrollEl) scrollEl.scrollTop = y;
      else window.scrollTo(0, y);
    };
    let lockedY = getY();

    const blockInput = (e) => e.preventDefault();
    const SCROLL_KEYS = new Set([
      "ArrowUp",
      "ArrowDown",
      "PageUp",
      "PageDown",
      "Home",
      "End",
      " ",
      "Spacebar",
    ]);
    const blockKeys = (e) => {
      // Don't swallow keys while the user is typing in a field.
      const tag = e.target && e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) {
        return;
      }
      if (SCROLL_KEYS.has(e.key)) e.preventDefault();
    };
    const onScroll = () => {
      // A scroll right after a tour-initiated one is the tour moving the
      // page — adopt the new position as the locked baseline.
      if (Date.now() - tourScrollAtRef.current < 700) {
        lockedY = getY();
        return;
      }
      if (getY() !== lockedY) setY(lockedY);
    };

    const target = scrollEl || window;
    window.addEventListener("wheel", blockInput, { passive: false });
    window.addEventListener("touchmove", blockInput, { passive: false });
    window.addEventListener("keydown", blockKeys, { passive: false });
    target.addEventListener("scroll", onScroll);
    // Mark the tour active on <body> so scroll-triggered page behaviors
    // (e.g. the salary page's infinite-scroll pagination) can pause while
    // the tour drives the scroll position — otherwise scrolling to a late
    // step keeps firing "load more", growing the page under the tour.
    document.body.classList.add("mmp-tour-active");
    return () => {
      window.removeEventListener("wheel", blockInput);
      window.removeEventListener("touchmove", blockInput);
      window.removeEventListener("keydown", blockKeys);
      target.removeEventListener("scroll", onScroll);
      document.body.classList.remove("mmp-tour-active");
    };
  }, [isOpen, scrollContainerSelector]);

  // The LAST section on a page can't scroll up under the header (the page is
  // already at max scroll), so it gets stuck mid-viewport where the tooltip
  // has no clean side and overlaps it. Add temporary bottom padding while the
  // tour is open so any section can be lifted into place; removed on close.
  //   - Mobile: enough room for the bottom sheet to sit below the section.
  //   - Desktop: ~a viewport of slack so the last section reaches the header
  //     and the tooltip fits below it. (Invisible — scroll is locked; removed
  //     on close.)
  useEffect(() => {
    if (!isOpen) return undefined;
    const isMobile = window.innerWidth <= MOBILE_MAX;
    const el =
      (scrollContainerSelector &&
        document.querySelector(scrollContainerSelector)) ||
      document.body;
    const prev = el.style.paddingBottom;
    const pad = isMobile
      ? TOOLTIP_HEIGHT_ESTIMATE + VIEWPORT_MARGIN * 3
      : Math.round(window.innerHeight * 0.85);
    el.style.paddingBottom = `${pad}px`;
    return () => {
      el.style.paddingBottom = prev;
    };
  }, [isOpen, scrollContainerSelector]);

  // Read back the tooltip's rendered box after each layout so the pointer arrow
  // can be positioned against its real edges — its height isn't known until it
  // renders, which is why the arrow previously only worked for the one
  // placement (below the card) whose anchor edge didn't depend on that height.
  // Guarded so it settles in a frame instead of looping.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = tooltipRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setTipRect((prev) =>
      prev &&
      Math.abs(prev.top - r.top) < 0.5 &&
      Math.abs(prev.left - r.left) < 0.5 &&
      Math.abs(prev.width - r.width) < 0.5 &&
      Math.abs(prev.height - r.height) < 0.5
        ? prev
        : {
            top: r.top,
            left: r.left,
            right: r.right,
            bottom: r.bottom,
            width: r.width,
            height: r.height,
          },
    );
  }, [isOpen, geo, step]);

  if (!isOpen || !geo || !steps.length) return null;

  const isLast = step === steps.length - 1;
  const current = steps[step];

  // Place the tooltip OUTSIDE the highlighted section box so it never
  // covers what's being shown. Try each side in priority order — below,
  // above, right, left — and take the first with enough clearance. Only
  // the last-resort clamp (nothing fits) can overlap. For the app's
  // two-column layout this reliably puts the tooltip beside the narrow
  // right-column sections and below/above the wide left-column ones.
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
  const vw = geo.viewportW;
  const vh = geo.viewportH;
  const cardTop = geo.card.top;
  const cardBottom = geo.card.top + geo.card.height;
  const cardLeft = geo.card.left;
  const cardRight = geo.card.left + geo.card.width;
  const needV = TOOLTIP_HEIGHT_ESTIMATE + TOOLTIP_GAP + VIEWPORT_MARGIN;
  const needH = TOOLTIP_WIDTH + TOOLTIP_GAP + VIEWPORT_MARGIN;
  const isMobile = vw <= MOBILE_MAX;

  let tooltipTop;
  let tooltipLeft;
  // Which tooltip edge the pointer arrow sits on, i.e. the direction it points
  // at the highlighted control: "up" (tooltip below card) / "down" (above) /
  // "left" (to the right of card) / "right" (to the left). Set in every branch
  // below so the arrow shows on every step and placement.
  let arrowDir = null;

  // On mobile the tooltip is a full-width sheet (CSS) on one edge. Top sheet
  // only when the control is near the top of its section AND there's real
  // card content below it to reveal; otherwise bottom sheet (the target was
  // scrolled just above it). Must match the same decision in scrollIntoView.
  const mobileSheetTop =
    isMobile &&
    (geo.target.top - geo.card.top) / Math.max(geo.card.height, 1) < 0.28 &&
    geo.card.top + geo.card.height - (geo.target.top + geo.target.height) >
      SHEET_TOP_MIN_BELOW;

  if (isMobile) {
    tooltipLeft = clamp(
      geo.target.left,
      VIEWPORT_MARGIN,
      vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
    );
    tooltipTop = mobileSheetTop
      ? HEADER_CLEARANCE
      : vh - TOOLTIP_HEIGHT_ESTIMATE - VIEWPORT_MARGIN;
    // Top sheet sits above the target → arrow on its bottom edge points down;
    // bottom sheet sits below the target → arrow on its top edge points up.
    arrowDir = mobileSheetTop ? "down" : "up";
  } else if (vh - cardBottom >= needV) {
    tooltipTop = cardBottom + TOOLTIP_GAP;
    tooltipLeft = clamp(
      geo.target.left,
      VIEWPORT_MARGIN,
      vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
    );
    arrowDir = "up";
  } else if (cardTop >= needV) {
    tooltipTop = cardTop - TOOLTIP_GAP - TOOLTIP_HEIGHT_ESTIMATE;
    tooltipLeft = clamp(
      geo.target.left,
      VIEWPORT_MARGIN,
      vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
    );
    arrowDir = "down";
  } else if (vw - cardRight >= needH) {
    tooltipLeft = cardRight + TOOLTIP_GAP;
    tooltipTop = clamp(
      geo.target.top,
      VIEWPORT_MARGIN,
      vh - TOOLTIP_HEIGHT_ESTIMATE - VIEWPORT_MARGIN,
    );
    arrowDir = "left";
  } else if (cardLeft >= needH) {
    tooltipLeft = cardLeft - TOOLTIP_GAP - TOOLTIP_WIDTH;
    tooltipTop = clamp(
      geo.target.top,
      VIEWPORT_MARGIN,
      vh - TOOLTIP_HEIGHT_ESTIMATE - VIEWPORT_MARGIN,
    );
    arrowDir = "right";
  } else {
    tooltipTop = clamp(
      cardBottom + TOOLTIP_GAP,
      VIEWPORT_MARGIN,
      vh - TOOLTIP_HEIGHT_ESTIMATE - VIEWPORT_MARGIN,
    );
    tooltipLeft = clamp(
      geo.target.left,
      VIEWPORT_MARGIN,
      vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
    );
    arrowDir = "up";
  }
  // Mobile: top sheet pins by its top edge (just below the header); bottom
  // sheet pins by its bottom edge so a taller-than-estimated tooltip grows
  // upward and stays fully on-screen. Desktop pins by top.
  let tooltipStyle;
  if (isMobile) {
    tooltipStyle = mobileSheetTop
      ? { top: HEADER_CLEARANCE }
      : { bottom: VIEWPORT_MARGIN };
  } else {
    tooltipStyle = { top: tooltipTop };
  }

  // Position the pointer arrow against the tooltip's MEASURED edges so it works
  // for every placement — below/above (arrow on top/bottom edge) and left/right
  // (arrow on left/right edge), plus the mobile sheets. The 12px square is
  // centred on the chosen edge; the tooltip paints over its inner half, leaving
  // a solid triangle pointing at the highlighted control. Hidden for the one
  // frame before the tooltip has been measured.
  const ARROW = 12;
  const ARROW_HALF = ARROW / 2;
  const EDGE_INSET = 14; // keep the arrow clear of the rounded corners
  const showArrow = !!tipRect && !!arrowDir;
  let arrowTop = 0;
  let arrowLeft = 0;
  if (showArrow) {
    if (arrowDir === "up" || arrowDir === "down") {
      const targetCenterX = geo.target.left + geo.target.width / 2;
      arrowLeft = clamp(
        targetCenterX - ARROW_HALF,
        tipRect.left + EDGE_INSET,
        tipRect.right - EDGE_INSET - ARROW,
      );
      arrowTop =
        (arrowDir === "up" ? tipRect.top : tipRect.bottom) - ARROW_HALF;
    } else {
      const targetCenterY = geo.target.top + geo.target.height / 2;
      arrowTop = clamp(
        targetCenterY - ARROW_HALF,
        tipRect.top + EDGE_INSET,
        tipRect.bottom - EDGE_INSET - ARROW,
      );
      arrowLeft =
        (arrowDir === "left" ? tipRect.left : tipRect.right) - ARROW_HALF;
    }
  }

  return createPortal(
    <div
      className="mmp-tour"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="mmp-tour__backdrop"
        style={{
          top: geo.card.top,
          left: geo.card.left,
          width: geo.card.width,
          height: geo.card.height,
          transition: `top 260ms ${EASE}, left 260ms ${EASE}, width 260ms ${EASE}, height 260ms ${EASE}`,
        }}
      />
      <div
        className="mmp-tour__ring"
        style={{
          top: geo.target.top,
          left: geo.target.left,
          width: geo.target.width,
          height: geo.target.height,
          transition: `top 260ms ${EASE}, left 260ms ${EASE}, width 260ms ${EASE}, height 260ms ${EASE}`,
        }}
      />
      {showArrow && (
        <div
          className="mmp-tour__arrow"
          style={{
            top: arrowTop,
            left: arrowLeft,
            transition: `top 260ms ${EASE}, left 260ms ${EASE}`,
          }}
        />
      )}
      <div
        ref={tooltipRef}
        className="mmp-tour__tooltip"
        style={{ left: tooltipLeft, width: TOOLTIP_WIDTH, ...tooltipStyle }}
      >
        <div className="mmp-tour__head">
          <span className="mmp-tour__eyebrow">◆ Quick tour</span>
          <span className="mmp-tour__count">
            Step {step + 1} of {steps.length}
          </span>
        </div>
        <div className="mmp-tour__body">
          {current.icon && (
            <span className="mmp-tour__icon">{current.icon}</span>
          )}
          <div className="mmp-tour__copy">
            <div className="mmp-tour__title">{current.title}</div>
            <div className="mmp-tour__text">{current.text}</div>
          </div>
        </div>
        <div className="mmp-tour__dots">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`mmp-tour__dot${i === step ? " is-active" : ""}`}
            />
          ))}
        </div>
        <div className="mmp-tour__actions">
          <button type="button" className="mmp-tour__skip" onClick={onDone}>
            Skip
          </button>
          <button
            type="button"
            className="mmp-tour__next"
            onClick={() => (isLast ? onDone() : setStep((v) => v + 1))}
          >
            {isLast ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
